import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { CloseConfirmationDropdown } from '../CloseConfirmationDropdown/CloseConfirmationDropdown';
import CustomButton from '../Button/CustomButton';
import { BRAND_MODAL_ACCENT, BRAND_MODAL_ACCENT_HOVER } from '../../pages/BrandDashboard/brandDashboardTokens';
import { EDITORIAL } from '../FormGenerator/editorialFormStyles';

export interface IGenericModalWrapper {
    onBack: () => void;
    children?: React.ReactNode;
    header?: string;
    leadingText?: string;
    onClose: () => void;
    hasUnsavedChanges?: boolean;
    formData?: Record<string, any>;
    handleOnSubmit?: (...args: any) => void;
    disableButton?: boolean;
    loading?: boolean;
    hideGlobalButton?: boolean;
    /** Match brand dashboard modals (audition / UGC) chrome */
    uiVariant?: 'default' | 'brand';
    /** Match header/footer to editorial form panel (single charcoal surface) */
    editorialChrome?: boolean;
}

const FOOTER_BUTTON_HEIGHT = 60; // px
// const FOOTER_VERTICAL_PADDING = 24; // 12px top + 12px bottom (py:1.5 -> 1.5*8)
// const FOOTER_TOTAL_HEIGHT = FOOTER_BUTTON_HEIGHT + FOOTER_VERTICAL_PADDING; // 84px

const GenericModalWrapper: React.FC<IGenericModalWrapper> = ({
    onBack,
    children,
    header,
    leadingText,
    onClose,
    hasUnsavedChanges = true,
    formData,
    handleOnSubmit,
    disableButton,
    loading,
    hideGlobalButton = true,
    editorialChrome = false,
    uiVariant,
}) => {
    const { themeStyle } = useThemeContext();
    const colors = theme[themeStyle];
    const chromeBg = editorialChrome ? EDITORIAL.background : colors.secondaryBg;
    const isBrand = uiVariant === 'brand';
    const saveDisabled = Boolean(disableButton || loading);

    const brandSaveButtonSx = isBrand
        ? saveDisabled
            ? {
                  backgroundColor: colors.darkGray,
                  border: 'none',
                  color: colors.lightWhite,
                  WebkitTextFillColor: colors.lightWhite,
                  '& .MuiTypography-root': {
                      color: colors.lightWhite,
                      WebkitTextFillColor: colors.lightWhite,
                  },
                  '&:hover': {
                      backgroundColor: colors.darkGray,
                      border: 'none',
                      color: colors.lightWhite,
                      WebkitTextFillColor: colors.lightWhite,
                  },
                  '&.Mui-disabled': {
                      backgroundColor: colors.darkGray,
                      color: colors.lightWhite,
                      opacity: 1,
                      border: 'none',
                  },
              }
            : {
                  backgroundColor: BRAND_MODAL_ACCENT,
                  border: 'none',
                  color: '#5f4200',
                  WebkitTextFillColor: '#5f4200',
                  '& .MuiTypography-root': {
                      color: '#5f4200',
                      WebkitTextFillColor: '#5f4200',
                  },
                  '&:hover': {
                      backgroundColor: BRAND_MODAL_ACCENT_HOVER,
                      border: 'none',
                      color: '#5f4200',
                      WebkitTextFillColor: '#5f4200',
                  },
              }
        : undefined;

    return (
        <Box
            sx={{
                width: '100%',
                flexShrink: 0,
                minHeight: '500px',
                maxHeight: { xs: '100dvh' },
                boxSizing: 'border-box',
                ...(isBrand ? { maxWidth: '100%' } : {}),
            }}>
            {/* Sticky Header */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: isBrand ? 3 : 2,
                    px: isBrand ? 0 : 1,
                    py: 0.5,
                    bgcolor: chromeBg,
                }}>
                <IconButton onClick={onBack} sx={{ color: colors.tag_white }}>
                    <ArrowBackIcon />
                </IconButton>

                <Typography
                    variant="h6"
                    sx={{
                        color: colors.tag_white,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'capitalize',
                    }}>
                    {header}
                </Typography>

                {/* Keep close button in the header row; no absolute positioning needed */}
                <CloseConfirmationDropdown
                    onClose={onClose}
                    hasUnsavedChanges={hasUnsavedChanges}
                    formData={formData!}
                    sx={{ cursor: 'pointer' }}
                />
            </Box>

            {leadingText && (
                <Typography
                    variant="h6"
                    sx={{
                        color: colors.tag_white,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 14,
                        mb: isBrand ? 3.5 : 1,
                        mt: isBrand ? 1 : 0,
                    }}>
                    {leadingText}
                </Typography>
            )}

            {children}

            {/* Sticky Footer */}
            {!hideGlobalButton && (
                <Box
                    component="footer"
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 3,
                        px: isBrand ? 0 : 2,
                        py: 1.5,
                        // optional backdrop for glassy look:
                        // backdropFilter: 'saturate(180%) blur(10px)',
                        bgcolor: chromeBg,
                    }}>
                    <CustomButton
                        label="Save"
                        onClick={handleOnSubmit}
                        customStyle={{ width: '100%', height: `${FOOTER_BUTTON_HEIGHT}px` }}
                        disabled={disableButton}
                        loading={loading}
                        sx={brandSaveButtonSx}
                    />
                </Box>
            )}
        </Box>
    );
};

export default GenericModalWrapper;
